import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Radio, Users, Clock, CheckCircle2, LogOut, AlertTriangle, ShieldAlert, MessageCircle, TrendingUp, RefreshCw } from 'lucide-react';
import { Card } from '../components/ui/card';
import { getAdminHeaders } from '../lib/auth';
import Navigation from '../components/Navigation';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EncontroAoVivo = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/checkin/live-dashboard`, { headers: getAdminHeaders() });
      setData(res.data);
      setLastRefresh(new Date());
    } catch { }
    setLoading(false);
  }, []);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  // Auto-refresh every 8 seconds
  useEffect(() => {
    const interval = setInterval(fetchDashboard, 8000);
    return () => clearInterval(interval);
  }, [fetchDashboard]);

  if (loading) return <div className="min-h-screen bg-[#F9F9F7] flex items-center justify-center"><RefreshCw className="w-8 h-8 animate-spin text-[#1A4D2E]" /></div>;

  const stats = data?.stats || {};
  const notPickedUp = data?.not_picked_up || [];
  const withObs = data?.with_observations || [];
  const checkins = data?.checkins || [];
  const meetingEnded = data?.meeting_ended || false;

  const present = checkins.filter(c => c.status === 'checked_in');
  const checkoutReq = checkins.filter(c => c.status === 'checkout_requested');

  return (
    <div className="min-h-screen bg-[#0D1117] pb-24">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1A4D2E] to-[#2D7A4A] text-white px-4 sm:px-6 py-5 sm:py-6">
        <div className="max-w-6xl mx-auto">
          <button onClick={() => navigate(-1)} className="hover:bg-white/10 rounded-full p-2 transition-all mb-2" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative">
                <Radio className="w-6 h-6" />
                <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse" />
              </div>
              <div>
                <h1 className="font-nunito font-bold text-2xl sm:text-3xl">Encontro ao Vivo</h1>
                <p className="font-inter text-white/60 text-xs">
                  Atualizado {lastRefresh ? lastRefresh.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '...'}
                </p>
              </div>
            </div>
            <button onClick={fetchDashboard} className="bg-white/10 hover:bg-white/20 rounded-xl px-3 py-2 text-sm font-semibold flex items-center gap-2 transition-all" data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4" /> Atualizar
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-5 space-y-4">
        {/* Alert: Not picked up */}
        {meetingEnded && notPickedUp.length > 0 && (
          <Card className="bg-red-950 border-2 border-red-500 rounded-2xl p-4 animate-pulse" data-testid="alert-not-picked-up">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-red-500 rounded-full p-2"><ShieldAlert className="w-5 h-5 text-white" /></div>
              <div>
                <h3 className="font-nunito font-bold text-red-400 text-base">Criancas nao retiradas!</h3>
                <p className="text-red-400/70 text-xs">O encontro terminou e estas criancas ainda estao presentes</p>
              </div>
            </div>
            <div className="space-y-2">
              {notPickedUp.map((c, i) => (
                <div key={i} className="bg-red-900/50 rounded-xl px-4 py-2.5 flex items-center justify-between">
                  <div>
                    <p className="font-nunito font-bold text-white text-sm">{c.child_name} <span className="text-red-400/60 font-mono text-xs ml-1">{c.child_codigo}</span></p>
                    <p className="text-red-400/60 text-xs">Responsavel: {c.parent_name || '-'}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-red-300 text-xs font-semibold">
                      {c.checkin_time ? new Date(c.checkin_time).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '-'}
                    </p>
                    {c.status === 'checkout_requested' && <span className="text-blue-400 text-[10px]">Saida pedida</span>}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="stats-grid">
          <Card className="bg-[#161B22] border border-[#30363D] rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-green-400" />
              <span className="text-[10px] text-gray-500 font-semibold uppercase">Presentes</span>
            </div>
            <p className="font-nunito font-black text-3xl text-green-400">{stats.present}</p>
            <p className="text-[10px] text-gray-500">de {stats.total_registered} criancas</p>
          </Card>

          <Card className="bg-[#161B22] border border-[#30363D] rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-amber-400" />
              <span className="text-[10px] text-gray-500 font-semibold uppercase">Aguardando</span>
            </div>
            <p className="font-nunito font-black text-3xl text-amber-400">{stats.pending}</p>
            <p className="text-[10px] text-gray-500">QR gerado</p>
          </Card>

          <Card className="bg-[#161B22] border border-[#30363D] rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-emerald-400" />
              <span className="text-[10px] text-gray-500 font-semibold uppercase">Pontualidade</span>
            </div>
            <p className="font-nunito font-black text-3xl text-emerald-400">{stats.punctuality_rate}%</p>
            <p className="text-[10px] text-gray-500">{stats.punctual} pontuais</p>
          </Card>

          <Card className="bg-[#161B22] border border-[#30363D] rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <LogOut className="w-4 h-4 text-blue-400" />
              <span className="text-[10px] text-gray-500 font-semibold uppercase">Sairam</span>
            </div>
            <p className="font-nunito font-black text-3xl text-blue-400">{stats.checked_out}</p>
            <p className="text-[10px] text-gray-500">{stats.checkout_requested} saida pedida</p>
          </Card>
        </div>

        {/* Observations Alert */}
        {withObs.length > 0 && (
          <Card className="bg-amber-950/50 border border-amber-700/50 rounded-xl p-4" data-testid="observations-alert">
            <div className="flex items-center gap-2 mb-2">
              <MessageCircle className="w-4 h-4 text-amber-400" />
              <h3 className="text-amber-400 font-bold text-sm">Observacoes Importantes ({withObs.length})</h3>
            </div>
            <div className="space-y-1.5">
              {withObs.map((c, i) => (
                <div key={i} className="bg-amber-900/30 rounded-lg px-3 py-2">
                  <p className="text-amber-200 text-xs font-bold">{c.child_name}</p>
                  <p className="text-amber-300/70 text-xs">{c.observacoes}</p>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Two columns: Present + Checkout Requested */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Present */}
          <div>
            <h3 className="font-nunito font-bold text-sm text-green-400 mb-2 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" /> Presentes ({present.length})
            </h3>
            <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
              {present.length === 0 ? (
                <p className="text-gray-600 text-xs text-center py-4">Nenhuma crianca presente</p>
              ) : present.map(c => (
                <Card key={c.id} className={`bg-[#161B22] border rounded-lg px-3 py-2 flex items-center justify-between ${meetingEnded ? 'border-red-500/50' : 'border-[#30363D]'}`}>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-green-500" />
                    <div>
                      <p className="text-white text-xs font-bold">{c.child_name}</p>
                      <p className="text-gray-500 text-[10px]">{c.parent_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {c.observacoes && <span className="bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded text-[9px]">Obs</span>}
                    {c.pontualidade && <span className="bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded text-[9px]">Pontual</span>}
                    <span className="text-gray-500 text-[10px]">
                      {c.checkin_time ? new Date(c.checkin_time).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '-'}
                    </span>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* Checkout Requested */}
          <div>
            <h3 className="font-nunito font-bold text-sm text-blue-400 mb-2 flex items-center gap-2">
              <LogOut className="w-4 h-4" /> Saida Solicitada ({checkoutReq.length})
            </h3>
            <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
              {checkoutReq.length === 0 ? (
                <p className="text-gray-600 text-xs text-center py-4">Nenhuma solicitacao</p>
              ) : checkoutReq.map(c => (
                <Card key={c.id} className="bg-[#161B22] border border-blue-500/30 rounded-lg px-3 py-2 flex items-center justify-between animate-pulse">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                    <div>
                      <p className="text-white text-xs font-bold">{c.child_name}</p>
                      <p className="text-gray-500 text-[10px]">{c.parent_name}</p>
                    </div>
                  </div>
                  <span className="text-blue-400 text-[10px] font-semibold">Aguardando saida</span>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>

      <Navigation active="" />
    </div>
  );
};

export default EncontroAoVivo;
